import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextInput from '../../components/text-input/text-input';
import Card from '../../components/card/card';
import CardContent from '../../components/card/card-content';
import CardHeader from '../../components/card/card-header';
import Paper from '../../components/paper/paper';
import Icon, { IconType } from '../../components/icons/icon';
import IconButton from '../../components/icons/icon-button';
import InfoIcon from '../../components/info-icon/info-icon';
import Tooltip from '../../components/tooltip/tooltip';

interface RuleOfThumbInputs {
  propertyValue: number;
  downPaymentPercent: number;
  interestRate: number;
  propertyTaxPercent: number;
  repairsPercent: number;
  landlordInsurancePercent: number;
  vacancyRate: number;
  propertyManagementPercent: number;
}

const validationSchema = yup.object({
  propertyValue: yup
    .number()
    .min(1, 'Property value must be greater than 0')
    .required('Property value is required'),
  downPaymentPercent: yup
    .number()
    .min(0, 'Down payment cannot be negative')
    .max(100, 'Down payment cannot exceed 100%')
    .required('Down payment is required'),
  interestRate: yup
    .number()
    .min(0, 'Interest rate cannot be negative')
    .max(50, 'Interest rate seems too high')
    .required('Interest rate is required'),
  propertyTaxPercent: yup
    .number()
    .min(0, 'Property tax cannot be negative')
    .max(10, 'Property tax seems too high')
    .required('Property tax is required'),
  repairsPercent: yup
    .number()
    .min(0, 'Repairs percentage cannot be negative')
    .max(20, 'Repairs percentage seems too high')
    .required('Repairs percentage is required'),
  landlordInsurancePercent: yup
    .number()
    .min(0, 'Insurance percentage cannot be negative')
    .max(5, 'Insurance percentage seems too high')
    .required('Insurance percentage is required'),
  vacancyRate: yup
    .number()
    .min(0, 'Vacancy rate cannot be negative')
    .max(100, 'Vacancy rate cannot exceed 100%')
    .required('Vacancy rate is required'),
  propertyManagementPercent: yup
    .number()
    .min(0, 'Management fee cannot be negative')
    .max(20, 'Management fee seems too high')
    .required('Management fee is required'),
});

interface RuleOfThumbInputsProps {
  onInputChange?: (data: RuleOfThumbInputs) => void;
}

const defaultValues: RuleOfThumbInputs = {
  propertyValue: 300000,
  downPaymentPercent: 20,
  interestRate: 7.0,
  propertyTaxPercent: 1.2,
  repairsPercent: 1.0,
  landlordInsurancePercent: 0.5,
  vacancyRate: 5,
  propertyManagementPercent: 8,
};

function RuleOfThumbInputs({ onInputChange }: RuleOfThumbInputsProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid, errors },
  } = useForm<RuleOfThumbInputs>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  // Watch all form values and call onInputChange when they change
  const watchedValues = watch();
  React.useEffect(() => {
    if (isValid && onInputChange) {
      onInputChange(watchedValues);
    }
  }, [watchedValues, isValid, onInputChange]);

  const onResetHandler = () => {
    reset(defaultValues);
  };

  return (
    <Paper
      elevation={3}
      style={{
        overflowY: 'auto',
        height: 'calc(100vh - 240px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
      className="bg-dark-secondary"
    >
      <Card
        style={{
          padding: '8px',
          boxShadow: 'none',
          borderRadius: '0px',
          border: 'none',
        }}
        className="bg-dark-secondary text-primary"
      >
        <form className="bg-dark-secondary">
          <CardHeader
            title="Inputs"
            className="text-primary"
            style={{ padding: '8px' }}
            action={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'end',
                }}
              >
                <Tooltip title="Reset">
                  <div>
                    <IconButton onClick={onResetHandler}>
                      <Icon
                        iconType={IconType.Refresh}
                        className="text-primary"
                      />
                    </IconButton>
                  </div>
                </Tooltip>
                <InfoIcon
                  title="Rule of Thumb Inputs"
                  body="These inputs help calculate the break-even rent percentage for quick property evaluation."
                />
              </div>
            }
          />

          <CardContent
            style={{ padding: '8px' }}
            className="white-inputs mui-dark-input"
          >
            <TextInput
              id="propertyValue"
              label="Property Value"
              type="number"
              prefix="$"
              {...register('propertyValue')}
              defaultValue={defaultValues.propertyValue}
              hasError={!!errors.propertyValue}
              errorMessage={errors.propertyValue?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="downPaymentPercent"
              label="Down Payment"
              type="number"
              prefix="%"
              {...register('downPaymentPercent')}
              defaultValue={defaultValues.downPaymentPercent}
              hasError={!!errors.downPaymentPercent}
              errorMessage={errors.downPaymentPercent?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="interestRate"
              label="Interest Rate"
              type="number"
              prefix="%"
              {...register('interestRate')}
              defaultValue={defaultValues.interestRate}
              hasError={!!errors.interestRate}
              errorMessage={errors.interestRate?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="propertyTaxPercent"
              label="Property Tax (% of Property Value)"
              type="number"
              prefix="%"
              {...register('propertyTaxPercent')}
              defaultValue={defaultValues.propertyTaxPercent}
              hasError={!!errors.propertyTaxPercent}
              errorMessage={errors.propertyTaxPercent?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="repairsPercent"
              label="Repairs (% of Property Value)"
              type="number"
              prefix="%"
              {...register('repairsPercent')}
              defaultValue={defaultValues.repairsPercent}
              hasError={!!errors.repairsPercent}
              errorMessage={errors.repairsPercent?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="landlordInsurancePercent"
              label="Landlord Insurance (% of Property Value)"
              type="number"
              prefix="%"
              {...register('landlordInsurancePercent')}
              defaultValue={defaultValues.landlordInsurancePercent}
              hasError={!!errors.landlordInsurancePercent}
              errorMessage={errors.landlordInsurancePercent?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="vacancyRate"
              label="Vacancy Rate"
              type="number"
              prefix="%"
              {...register('vacancyRate')}
              defaultValue={defaultValues.vacancyRate}
              hasError={!!errors.vacancyRate}
              errorMessage={errors.vacancyRate?.message}
              style={{ marginBottom: '16px' }}
            />

            <TextInput
              id="propertyManagementPercent"
              label="Property Management Fees (% of Rent)"
              type="number"
              prefix="%"
              {...register('propertyManagementPercent')}
              defaultValue={defaultValues.propertyManagementPercent}
              hasError={!!errors.propertyManagementPercent}
              errorMessage={errors.propertyManagementPercent?.message}
              style={{ marginBottom: '16px' }}
            />
          </CardContent>
        </form>
      </Card>
    </Paper>
  );
}

export default RuleOfThumbInputs;
export type { RuleOfThumbInputs as RuleOfThumbInputsData };
